import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Typo, SimpleButton, Flex, Icon, theme } from '../../../../ui';
import { media } from '../../../../utils/media';
import { MODULE_TYPES } from '../../../editCourse/moduleConfig';
import { MOCK_CONTENT, getModuleDisplayInfo } from '../../data/mockContent';
import { COURSE_TEMPLATES, TEMPLATE_CATEGORIES, applyTemplate } from '../../data/courseTemplates';
import {
  selectGuideTocStructure,
  selectGuidePageModules,
  selectGuideSelectedTemplate,
  assignModuleToPage,
  removeModuleFromPage,
  setTocStructure,
  setPageModuleAssignments,
  setSelectedTemplate,
  setGuideSettings,
} from '../../courseGuideState';

const StepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StepHeader = styled.div`
  margin-bottom: 8px;
`;

const SplitLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  ${media.lessThan('tablet')} {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModuleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
`;

const ModuleCard = styled.button`
  padding: 12px;
  border: 2px solid ${({ $selected, theme }) => ($selected ? theme.colors.PRIMARY : theme.colors.NEU_200)};
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  background: ${({ theme }) => theme.colors.NEU_0};
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.PRIMARY};
    background: ${({ theme }) => theme.colors.PRIMARY_LIGHT};
  }
`;

const PreviewPanel = styled.div`
  background: ${({ theme }) => theme.colors.NEU_50};
  border-radius: 8px;
  padding: 20px;
  min-height: 120px;
`;

const PreviewHtml = styled.div`
  font-size: ${({ theme }) => theme.fonts?.xSmall || '12px'};
`;

const TemplateCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 8px;
`;

const TemplateCard = styled.button`
  padding: 12px;
  border: 2px solid ${({ $active, theme }) => ($active ? theme.colors.PRIMARY : theme.colors.NEU_200)};
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  background: ${({ $active, theme }) => ($active ? theme.colors.PRIMARY_LIGHT : theme.colors.NEU_0)};
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.PRIMARY};
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const CategoryTab = styled.button`
  padding: 6px 14px;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.PRIMARY : theme.colors.NEU_200)};
  border-radius: 20px;
  background: ${({ $active, theme }) => ($active ? theme.colors.PRIMARY_LIGHT : theme.colors.NEU_0)};
  color: ${({ $active, theme }) => ($active ? theme.colors.PRIMARY : theme.colors.NEU_PRIMARY)};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fonts?.xSmall || '12px'};
  transition: all 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.PRIMARY};
  }
`;

const PageSelector = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PageBlock = styled.div`
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.PRIMARY : theme.colors.NEU_200)};
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  background: ${({ $active, theme }) => ($active ? theme.colors.PRIMARY_LIGHT : theme.colors.NEU_0)};
  transition: all 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.PRIMARY};
  }
`;

const ModulesContainer = styled.div`
  margin-top: 4px;
`;

const ModuleChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: ${({ theme }) => theme.colors.NEU_100};
  border-radius: 4px;
  font-size: ${({ theme }) => theme.fonts?.xSmall || '12px'};
  margin: 2px;
`;

const RemoveChipBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  font-size: ${({ theme }) => theme.fonts?.small || '14px'};
  color: ${({ theme }) => theme.colors.NEU_600};

  &:hover {
    color: ${({ theme }) => theme.colors.ERROR_RED};
  }
`;

const StepModuleShowcase = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const tocStructure = useSelector(selectGuideTocStructure);
  const pageModuleAssignments = useSelector(selectGuidePageModules);
  const selectedTemplate = useSelector(selectGuideSelectedTemplate);

  const [selectedPageId, setSelectedPageId] = useState(null);
  const [previewModule, setPreviewModule] = useState(null);
  const [activeCategory, setActiveCategory] = useState('generic');

  // Auto-select first page if none selected
  useEffect(() => {
    if (!selectedPageId && tocStructure.length > 0 && tocStructure[0].pages?.length > 0) {
      setSelectedPageId(tocStructure[0].pages[0].id);
    }
  }, [selectedPageId, tocStructure]);

  const handleAddModuleToPage = useCallback(
    (moduleType) => {
      if (!selectedPageId) return;
      dispatch(assignModuleToPage({ tempPageId: selectedPageId, moduleType }));
    },
    [dispatch, selectedPageId],
  );

  const handleRemoveModule = useCallback(
    (pageId, index) => {
      dispatch(removeModuleFromPage({ tempPageId: pageId, index }));
    },
    [dispatch],
  );

  const handleSelectTemplate = useCallback(
    (template) => {
      const { tocStructure: newToc, pageModuleAssignments: newAssignments } = applyTemplate(template, t);
      dispatch(setTocStructure(newToc));
      dispatch(setPageModuleAssignments(newAssignments));
      dispatch(setSelectedTemplate(template.id));
      // Auto-set quiz/diploma from template
      dispatch(
        setGuideSettings({
          quizEnabled: !!template.structure.suggestQuiz,
          diplomaEnabled: !!template.structure.suggestDiploma,
        }),
      );
      // Select the first page
      if (newToc[0]?.pages?.[0]) {
        setSelectedPageId(newToc[0].pages[0].id);
      }
    },
    [dispatch, t],
  );

  const filteredTemplates = COURSE_TEMPLATES.filter((tmpl) => tmpl.category === activeCategory);

  return (
    <StepWrapper>
      <StepHeader>
        <Typo.ContentBigHeader>{t('courseGuide.moduleShowcase.title')}</Typo.ContentBigHeader>
        <Typo.SmallText>{t('courseGuide.moduleShowcase.subtitle')}</Typo.SmallText>
      </StepHeader>

      {/* Templates section */}
      <div>
        <Typo.TextTitle $marginBottom='8px'>{t('courseGuide.moduleShowcase.templates')}</Typo.TextTitle>
        <CategoryTabs>
          {TEMPLATE_CATEGORIES.map((cat) => (
            <CategoryTab key={cat.id} $active={activeCategory === cat.id} onClick={() => setActiveCategory(cat.id)} type='button'>
              {t(cat.labelKey)}
            </CategoryTab>
          ))}
        </CategoryTabs>
        <TemplateCardsGrid>
          {filteredTemplates.map((template) => (
            <TemplateCard key={template.id} $active={selectedTemplate === template.id} onClick={() => handleSelectTemplate(template)} type='button'>
              <Typo.SmallText fontWeight={600}>{t(template.nameKey)}</Typo.SmallText>
              <Typo.Info>{t(template.descriptionKey)}</Typo.Info>
            </TemplateCard>
          ))}
        </TemplateCardsGrid>
      </div>

      <SplitLayout>
        {/* Left: Available modules + preview */}
        <Panel>
          <Typo.TextTitle>{t('courseGuide.moduleShowcase.availableModules')}</Typo.TextTitle>
          <Typo.SmallText>{t('courseGuide.moduleShowcase.clickToAdd')}</Typo.SmallText>

          <ModuleGrid>
            {MODULE_TYPES.map(({ type, labelKey }) => {
              const { icon } = getModuleDisplayInfo(type);
              const IconComp = Icon[icon];
              return (
                <ModuleCard
                  key={type}
                  onClick={() => handleAddModuleToPage(type)}
                  onMouseEnter={() => setPreviewModule(type)}
                  $selected={previewModule === type}
                  type='button'
                  disabled={!selectedPageId}
                >
                  {IconComp && <IconComp size='small' />}
                  <Typo.SmallText>{t(labelKey)}</Typo.SmallText>
                </ModuleCard>
              );
            })}
          </ModuleGrid>

          {previewModule && MOCK_CONTENT[previewModule] && (
            <PreviewPanel>
              <Typo.TextTitle $marginBottom='8px'>{t('courseGuide.moduleShowcase.previewTitle')}</Typo.TextTitle>
              {MOCK_CONTENT[previewModule].previewContentKey ? (
                <PreviewHtml dangerouslySetInnerHTML={{ __html: t(MOCK_CONTENT[previewModule].previewContentKey) }} />
              ) : (
                <Typo.SmallText>{t(MOCK_CONTENT[previewModule].previewCaptionKey)}</Typo.SmallText>
              )}
            </PreviewPanel>
          )}
        </Panel>

        {/* Right: Page assignments */}
        <Panel>
          <Typo.TextTitle>{t('courseGuide.moduleShowcase.pageAssignments')}</Typo.TextTitle>

          {tocStructure.length === 0 ? (
            <Typo.SmallText>{t('courseGuide.moduleShowcase.selectPage')}</Typo.SmallText>
          ) : (
            <PageSelector>
              {tocStructure.map((section) => (
                <div key={section.id}>
                  <Typo.SmallText fontWeight={600} $marginBottom='4px'>
                    {section.name}
                  </Typo.SmallText>
                  {section.pages.map((page) => {
                    const modules = pageModuleAssignments[page.id] || [];
                    const isActive = selectedPageId === page.id;
                    return (
                      <PageBlock key={page.id} $active={isActive} onClick={() => setSelectedPageId(page.id)}>
                        <Typo.SmallText fontWeight={isActive ? 600 : 400}>{page.name}</Typo.SmallText>
                        <ModulesContainer>
                          {modules.length === 0 ? (
                            <Typo.Info color={theme.colors.NEU_300}>{t('courseGuide.moduleShowcase.noModulesAssigned')}</Typo.Info>
                          ) : (
                            modules.map((mod, idx) => (
                              <ModuleChip key={idx}>
                                {mod.moduleType}
                                <RemoveChipBtn
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveModule(page.id, idx);
                                  }}
                                >
                                  &times;
                                </RemoveChipBtn>
                              </ModuleChip>
                            ))
                          )}
                        </ModulesContainer>
                      </PageBlock>
                    );
                  })}
                </div>
              ))}
            </PageSelector>
          )}
        </Panel>
      </SplitLayout>
    </StepWrapper>
  );
};

export default StepModuleShowcase;
